/**
 * English-language SEO landing pages (Belgrade / expat audience).
 *
 * Pure data — no React, no i18n, no image imports — so the same module is
 * consumed by the runtime page (src/pages/EnglishLanding.tsx) AND by the
 * build-time prerender (scripts/prerender-en.tsx) that writes static HTML
 * with the head + full content for crawlers that do not execute JS.
 *
 * Prices mirror src/pages/Pricing.tsx (Serbian price list, 2026). Do not
 * change a price here without changing it there.
 */

import { SITE_URL } from '@/lib/siteDomains';

/** All pages, including English landings, are indexed on the single .rs domain. */
export const BASE_URL = SITE_URL;

export const CLINIC = {
  name: 'PHYSIOWRLD',
  legalName: 'PHYSIOWRLD – Fizikalna terapija Novi Beograd',
  street: 'Omladinskih brigada 96E/4',
  block: 'Block 64, New Belgrade',
  city: 'Novi Beograd',
  postalCode: '11070',
  country: 'RS',
  phoneDisplay: '+381 60 503 74 77',
  phoneE164: '+381605037477',
  whatsappBase: 'https://wa.me/381605037477',
  email: 'info@physiowrld.rs',
  geo: { lat: 44.8187721, lng: 20.3974697 },
  hours: [
    { days: 'Monday – Friday', open: '08:00', close: '20:00', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] },
    { days: 'Saturday', open: '09:00', close: '15:00', dayOfWeek: ['Saturday'] },
  ],
  closed: 'Sunday – closed',
  parking: 'Free street parking directly in front of the building; a public garage is a 2-minute walk away.',
  mapsLink: 'https://www.google.com/maps/place/PHYSIOWRLD/@44.810219,20.403981',
  mapsEmbed:
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2830.0394887956847!2d20.397469776556447!3d44.81877217107158!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x475a7013e0c24b05%3A0x8f3c9e5c7b8d9a6e!2sOmladinskih%20brigada%2096E%2C%20Beograd%2C%20Serbia!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s',
  sameAs: ['https://www.facebook.com/share/19X4S9QEjy', 'https://www.instagram.com/physiowrld'],
  logo: `${BASE_URL}/images/physiowrld-logo.webp`,
  ogImage: `${BASE_URL}/images/physiowrld-klinika-novi-beograd-og.webp`,
};

export const TEAM = [
  {
    slug: 'filip-stanojevic',
    name: 'Filip Stanojević',
    role: 'Licensed physiotherapist',
    note: 'Leads assessments, kinesitherapy and post-operative rehabilitation. Consultations in English.',
  },
  {
    slug: 'stefan-miljkovic',
    name: 'Stefan Miljković',
    role: 'Ackerman chiropractor',
    note: 'Manual therapy and spinal adjustments after a structured clinical screening.',
  },
  {
    slug: 'snezana-sainovic',
    name: 'Snežana Šainović',
    role: 'Massage therapist',
    note: 'Therapeutic and sports massage, manual lymphatic drainage.',
  },
];

export interface EnPriceRow {
  service: string;
  duration: string;
  price: string;
  pack?: string;
  href?: string;
}

/** English price list — mirrors /cenovnik exactly. */
export const EN_PRICES: EnPriceRow[] = [
  { service: 'Physical therapy (assessment + device therapy + manual techniques)', duration: '30 min', price: '2,500 RSD', pack: '10 sessions – 20,000 RSD', href: '/physical-therapy-belgrade' },
  { service: 'Kinesitherapy (one-on-one guided exercise)', duration: '30 min', price: '3,500 RSD', pack: '10 sessions – 30,000 RSD', href: '/physical-therapy-belgrade' },
  { service: 'Post-operative rehabilitation (knee, hip, shoulder, arthroscopy)', duration: '30 min', price: '3,500 RSD', pack: '10 sessions – 30,000 RSD', href: '/acl-knee-rehabilitation-belgrade' },
  { service: 'Chiropractic – manual adjustment (incl. screening)', duration: '30 min', price: '5,000 RSD', href: '/chiropractor-belgrade' },
  { service: 'Active Release technique', duration: '30 min', price: '4,000 RSD', href: '/sports-injury-rehabilitation-belgrade' },
  { service: 'Shockwave therapy (radial ESWT)', duration: '15–20 min', price: '3,000 RSD', pack: '5 sessions – 13,500 RSD', href: '/heel-spur-shockwave-therapy-belgrade' },
  { service: 'High-intensity laser therapy (HILT)', duration: '15–20 min', price: '2,500 RSD', pack: '10 sessions – 20,000 RSD', href: '/laser-terapija-novi-beograd' },
  { service: 'Electrotherapy (TENS / interferential / electrostimulation)', duration: '20 min', price: '2,500 RSD', pack: '10 sessions – 20,000 RSD', href: '/physical-therapy-belgrade' },
  { service: 'Therapeutic massage', duration: '30 / 60 min', price: '3,500 / 5,000 RSD', href: '/masaza-novi-beograd' },
  { service: 'Sports massage', duration: '30 / 60 min', price: '3,500 / 5,000 RSD', href: '/masaza-novi-beograd' },
  { service: 'Relaxation massage', duration: '30 / 60 min', price: '3,000 / 4,000 RSD', href: '/masaza-novi-beograd' },
  { service: 'Manual lymphatic drainage', duration: '30 / 60 min', price: '3,500 / 6,000 RSD', href: '/limfna-drenaza-novi-beograd' },
  { service: 'Pressotherapy (device lymphatic drainage)', duration: '20 min', price: '2,000 RSD', pack: '10 sessions – 15,000 RSD', href: '/presoterapija-novi-beograd' },
  { service: 'Diagnostic consultation (standalone)', duration: '30 min', price: '3,000 RSD', href: '/dijagnostika' },
];

export interface EnFaq {
  q: string;
  a: string;
}

export interface EnSection {
  id: string;
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
  table?: { caption?: string; headers: string[]; rows: string[][] };
}

export interface EnLanding {
  slug: string;
  /** Serbian counterpart URL (hreflang sr / ru / x-default + language switcher). */
  srCounterpart: string;
  title: string;
  description: string;
  h1: string;
  subtitle: string;
  breadcrumb: string;
  serviceName: string;
  /** schema.org type for the main entity */
  schemaType: 'PhysicalTherapy' | 'MedicalTherapy' | 'Service';
  hero: { src: string; alt: string; width: number; height: number };
  intro: string[];
  whoFor: string[];
  firstVisit: string[];
  treatment: string[];
  durationAndCount: string[];
  prices: EnPriceRow[];
  conditions: string[];
  contraindications: string[];
  sections: EnSection[];
  faqs: EnFaq[];
  related: { label: string; href: string }[];
  whatsappText: string;
}

const WA = (msg: string) => `${CLINIC.whatsappBase}?text=${encodeURIComponent(msg)}`;

const price = (s: string) => EN_PRICES.find((p) => p.service.startsWith(s))!;

export const EN_LANDINGS: EnLanding[] = [
  // ───────────────────────────────────────────────────────────── 1
  {
    slug: 'physical-therapy-belgrade',
    srCounterpart: '/fizikalna-terapija-beograd',
    title: 'Physical Therapy Belgrade – English-Speaking Team | PHYSIOWRLD',
    description:
      'Physiotherapy and physical therapy in Belgrade with an English-speaking team. Assessment, manual therapy, kinesitherapy, laser and electrotherapy in Block 64, New Belgrade. From 2,500 RSD.',
    h1: 'Physiotherapy and Physical Therapy in Belgrade',
    subtitle:
      'One-on-one physiotherapy for pain, injury and post-operative recovery — assessment and treatment in English, in Block 64, New Belgrade.',
    breadcrumb: 'Physical Therapy Belgrade',
    serviceName: 'Physical therapy',
    schemaType: 'PhysicalTherapy',
    hero: {
      src: '/images/fizikalna-terapija-novi-beograd.webp',
      alt: 'Physiotherapist treating a patient at PHYSIOWRLD physical therapy clinic in New Belgrade',
      width: 850,
      height: 500,
    },
    intro: [
      'PHYSIOWRLD is a physiotherapy clinic in New Belgrade (Block 64) that treats musculoskeletal pain, sports injuries and post-operative conditions. Every session is 30 minutes of one-on-one time with a licensed physiotherapist — there is no group treatment and no unattended time on a device.',
      'Our team communicates in English, which matters when you need to describe symptoms precisely and understand your plan. Patients from the diplomatic community, international companies, students and visitors to Belgrade are a regular part of our practice.',
      'Treatment combines a clinical assessment, manual therapy, guided exercise (kinesitherapy) and, when indicated, device-based modalities such as high-intensity laser, electrotherapy and radial shockwave. The plan is written for your goal — walking without pain, returning to work, or getting back to sport — and re-checked as you progress.',
    ],
    whoFor: [
      'Back, neck and shoulder pain (acute or chronic)',
      'Sports injuries — muscle, tendon and ligament',
      'Recovery after knee, hip or shoulder surgery',
      'Joint stiffness and reduced range of motion',
      'Sciatica-type leg pain and disc-related back pain (after medical assessment)',
      'Expats and visitors who need a physiotherapist in Belgrade who speaks English',
    ],
    firstVisit: [
      'Your first appointment starts with a conversation: history of the problem, previous imaging or reports (bring them if you have them), medication, and what you need to get back to.',
      'The physiotherapist then performs a physical examination — posture, joint range of motion, muscle strength, palpation of the painful structures and functional tests relevant to your complaint.',
      'You leave with a clear explanation of what is likely causing the problem, a treatment plan with an estimated number of sessions, and usually a first treatment in the same visit. The assessment is included in the session price — it is not billed separately.',
    ],
    treatment: [
      'Manual therapy: soft-tissue techniques, joint mobilisation and Active Release for restricted or painful structures.',
      'Kinesitherapy: individually selected exercises performed with the therapist, progressed session by session.',
      'Device-based therapy when indicated: high-intensity laser (HILT), TENS / interferential electrotherapy, radial shockwave for chronic tendon problems.',
      'A short home programme — two to four exercises — so progress continues between visits.',
    ],
    durationAndCount: [
      'A session lasts 30 minutes. Acute problems typically need 4–6 sessions; chronic conditions and post-operative rehabilitation usually need 10 or more, scheduled 2–3 times per week at the start and less often as you improve.',
      'The number of sessions is an estimate given after the assessment and is reviewed as you progress — you are never committed to a package you do not need.',
    ],
    prices: [
      price('Physical therapy'),
      price('Kinesitherapy'),
      price('High-intensity laser'),
      price('Electrotherapy'),
      price('Shockwave therapy'),
    ],
    conditions: [
      'Low back pain and lumbar disc problems',
      'Neck pain, cervical syndrome and tension headache',
      'Shoulder impingement, rotator cuff injury, frozen shoulder',
      'Tennis and golfer’s elbow',
      'Knee pain, meniscus and ligament injuries, patellofemoral pain',
      'Ankle sprains and Achilles tendinopathy',
      'Plantar fasciitis and heel spur',
      'Post-fracture stiffness (wrist, ankle, hip)',
      'Post-operative rehabilitation (ACL, meniscus, joint replacement, arthroscopy)',
    ],
    contraindications: [
      'Acute fracture that has not been assessed and stabilised by a doctor',
      'Fever, active infection or unexplained weight loss',
      'Sudden loss of bladder or bowel control, or progressive numbness in both legs — go to an emergency department',
      'Chest pain, shortness of breath or calf swelling with warmth (possible thrombosis) — seek medical care first',
      'Active cancer in the treated region without oncologist clearance for physiotherapy',
    ],
    sections: [
      {
        id: 'physiotherapy-vs-physical-therapy',
        heading: 'Physiotherapy or physical therapy — is there a difference?',
        paragraphs: [
          'In Serbia, as in most of Europe, the terms are used interchangeably. “Fizikalna terapija” historically referred to device-based modalities (electrotherapy, ultrasound, laser), while “fizioterapija” emphasised manual therapy and exercise. At PHYSIOWRLD both are part of the same 30-minute session: the assessment decides what your tissue needs on a given day, not the name on the door.',
          'If you are used to the North American model, our sessions are closest to an outpatient orthopaedic physical therapy visit — one licensed therapist, one patient, hands-on treatment plus exercise.',
        ],
      },
      {
        id: 'what-to-bring',
        heading: 'What to bring and how to prepare',
        bullets: [
          'Any MRI, X-ray or ultrasound reports and discharge letters (a photo on your phone is fine).',
          'Comfortable clothing that allows access to the treated area — shorts for knee and hip, a vest top for shoulder and neck.',
          'A list of current medication, especially anticoagulants or corticosteroids.',
          'If you do not have imaging and it is needed, our partner diagnostic clinic (Medicor, Bulevar umetnosti 15) is a 5-minute drive away — see the diagnostics page.',
        ],
      },
      {
        id: 'new-belgrade-location',
        heading: 'Physical therapy in New Belgrade — getting here',
        paragraphs: [
          'The clinic is in Block 64, at Omladinskih brigada 96E/4, a few minutes from the Belgrade Arena, the Ušće business district and the Airport City complex. Typical travel times: 5–10 minutes by car from Blocks 45, 61–63 and 70; 10–15 minutes from Zemun and Bežanijska kosa; 15–25 minutes from the city centre (Stari grad, Vračar) depending on traffic. Bus lines 73, 88 and 89 stop within a short walk.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Does the physiotherapist speak English?',
        a: 'Yes. Assessment, treatment explanations and written home programmes are provided in English. You do not need an interpreter.',
      },
      {
        q: 'How much does physical therapy cost in Belgrade at PHYSIOWRLD?',
        a: 'A 30-minute physical therapy session is 2,500 RSD (10 sessions 20,000 RSD). Kinesitherapy and post-operative rehabilitation are 3,500 RSD per session (10 sessions 30,000 RSD). The clinical assessment is included in the first session.',
      },
      {
        q: 'Do I need a referral from a doctor?',
        a: 'No referral is required for private physiotherapy in Serbia. If your problem needs medical imaging or a specialist opinion first, we will tell you at the assessment and can direct you to our partner diagnostic clinic.',
      },
      {
        q: 'How many sessions will I need?',
        a: 'Most acute problems settle in 4–6 sessions. Chronic pain and post-operative rehabilitation typically need 10 or more. You receive an estimate after the assessment, and it is reviewed as you progress.',
      },
      {
        q: 'Can I pay by card, and can I get an invoice for my insurance?',
        a: 'Yes — cash and card are accepted, and we issue an itemised invoice in English on request for reimbursement by international health insurers. Please check your policy’s physiotherapy terms in advance.',
      },
      {
        q: 'How quickly can I get an appointment?',
        a: 'Usually within 1–2 working days. Message us on WhatsApp with a short description of the problem and your preferred times.',
      },
      {
        q: 'Is there parking at the clinic?',
        a: 'Yes — street parking directly in front of the building and a public garage within a 2-minute walk.',
      },
    ],
    related: [
      { label: 'English-speaking physiotherapist', href: '/english-speaking-physiotherapist-belgrade' },
      { label: 'Sports injury rehabilitation', href: '/sports-injury-rehabilitation-belgrade' },
      { label: 'ACL rehabilitation', href: '/acl-knee-rehabilitation-belgrade' },
      { label: 'Chiropractic and manual therapy', href: '/chiropractor-belgrade' },
      { label: 'Shockwave therapy', href: '/heel-spur-shockwave-therapy-belgrade' },
      { label: 'Full price list in English', href: '/prices' },
    ],
    whatsappText: 'Hello, I would like to book a physiotherapy appointment in English.',
  },

  // ───────────────────────────────────────────────────────────── 2
  {
    slug: 'english-speaking-physiotherapist-belgrade',
    srCounterpart: '/fizikalna-terapija-novi-beograd',
    title: 'English-Speaking Physiotherapist Belgrade | PHYSIOWRLD',
    description:
      'English-speaking physiotherapist in Belgrade for expats, diplomats, students and visitors. Assessment and treatment in English, WhatsApp booking, Block 64 New Belgrade. Sessions from 2,500 RSD.',
    h1: 'English-Speaking Physiotherapist in Belgrade',
    subtitle:
      'Clinical assessment, hands-on treatment and rehabilitation plans explained in plain English — for foreigners living in or visiting Belgrade.',
    breadcrumb: 'English-Speaking Physiotherapist',
    serviceName: 'Physiotherapy in English',
    schemaType: 'PhysicalTherapy',
    hero: {
      src: '/images/prvi-dolazak-physiowrld.webp',
      alt: 'First physiotherapy consultation in English at PHYSIOWRLD, New Belgrade',
      width: 1600,
      height: 915,
    },
    intro: [
      'Finding a physiotherapist in Belgrade who speaks English well enough to discuss a diagnosis, explain a treatment plan and answer your questions is harder than it should be. At PHYSIOWRLD the whole process — booking, assessment, treatment and follow-up — can be done in English.',
      'We regularly treat embassy staff, employees of international companies in New Belgrade’s business district, exchange students, digital nomads and tourists who were injured while travelling. If you have arrived with an existing rehabilitation programme from home, we continue it rather than starting over.',
      'Booking is simple: send a WhatsApp message with a sentence about the problem and your preferred time, and we reply with available slots, usually the same day.',
    ],
    whoFor: [
      'Expats and diplomats based in Belgrade',
      'International students and Erasmus exchange participants',
      'Business travellers and remote workers staying for weeks or months',
      'Tourists injured during their stay who need immediate assessment',
      'Patients continuing rehabilitation started abroad (post-surgery, post-injury)',
      'Anyone who prefers to explain their symptoms in English',
    ],
    firstVisit: [
      'Book by WhatsApp, phone or the online form. Tell us briefly what happened and whether you have imaging or medical reports.',
      'At the clinic, the physiotherapist takes a detailed history and performs a physical examination in English. If you have foreign medical documents, we read them with you — there is no need to translate them.',
      'You receive a spoken and, on request, written explanation of the findings, a plan with an estimated number of sessions, and your first treatment. Prices are stated up-front; there is no separate assessment fee.',
    ],
    treatment: [
      'Manual therapy and Active Release for painful or restricted joints and soft tissue.',
      'Kinesitherapy — exercise performed with the therapist, adapted to your level and goals.',
      'Device-based therapy where indicated: high-intensity laser, electrotherapy, radial shockwave.',
      'Written home programme in English and, if you are leaving Belgrade, a summary you can hand to your physiotherapist at home.',
    ],
    durationAndCount: [
      'Each session is 30 minutes of one-on-one care. Short-stay visitors often book 2–4 sessions in a single week; residents follow the normal course of 4–10 sessions depending on the condition.',
    ],
    prices: [price('Physical therapy'), price('Kinesitherapy'), price('Chiropractic'), price('Shockwave therapy'), price('Therapeutic massage')],
    conditions: [
      'Acute back and neck pain, including after long flights',
      'Sports and running injuries',
      'Sprains, strains and post-fracture stiffness',
      'Shoulder, elbow and wrist pain from desk work',
      'Post-operative rehabilitation started abroad',
      'Chronic tendon problems (Achilles, plantar fascia, tennis elbow)',
    ],
    contraindications: [
      'Injuries with suspected fracture or dislocation — go to an emergency department first (Urgentni centar, Pasterova 2)',
      'Severe, worsening neurological symptoms (weakness, numbness, loss of bladder control)',
      'Fever or signs of infection in the painful area',
      'Chest pain or breathlessness',
    ],
    sections: [
      {
        id: 'insurance',
        heading: 'International health insurance and invoices',
        paragraphs: [
          'We are a private clinic and do not bill insurers directly. We provide an itemised invoice in English with the clinic’s registration details, the treatment codes and dates, which most international insurers (Cigna, Allianz, Bupa, AXA and employer schemes) accept for reimbursement. Check whether your policy requires a doctor’s referral before physiotherapy — some do.',
        ],
      },
      {
        id: 'foreign-reports',
        heading: 'Bringing medical documents from abroad',
        paragraphs: [
          'MRI, X-ray and surgical reports in English, German, French, Italian or Russian can be reviewed as they are. If your surgeon provided a rehabilitation protocol, bring it — we follow the operating surgeon’s restrictions on load and range of motion rather than substituting our own timeline.',
        ],
      },
      {
        id: 'how-to-reach',
        heading: 'How to reach us from central Belgrade',
        bullets: [
          'By taxi or car: 15–25 minutes from Knez Mihailova / Terazije, 10 minutes from Ušće and the Belgrade Waterfront via the Gazela or Ada bridge.',
          'By bus: lines 73, 88 and 89 stop near Omladinskih brigada; the clinic entrance is in Block 64, building 96E, entrance 4.',
          'Parking: free street parking in front of the building; a public garage is 2 minutes on foot.',
          'Say “Block 64, Omladinskih brigada 96E” to any taxi driver — the address is well known.',
        ],
      },
    ],
    faqs: [
      {
        q: 'Is the entire visit really in English?',
        a: 'Yes. The physiotherapist explains the assessment, the plan and every exercise in English. Reception and WhatsApp booking are also in English.',
      },
      {
        q: 'Can I book on WhatsApp?',
        a: 'Yes. Send a message to +381 60 503 74 77 with a sentence about your problem and preferred times. You normally get a reply with available slots within a few hours during working hours.',
      },
      {
        q: 'I am only in Belgrade for a week. Is it worth coming?',
        a: 'Yes — an assessment plus 2–4 treatments over a week can settle an acute problem and gives you a clear home programme and a written summary for your physiotherapist at home.',
      },
      {
        q: 'How much does a session cost?',
        a: 'Physical therapy is 2,500 RSD for 30 minutes; kinesitherapy and post-operative rehabilitation are 3,500 RSD. Chiropractic is 5,000 RSD, shockwave 3,000 RSD. The full list is on the English price list page.',
      },
      {
        q: 'Do you accept international insurance?',
        a: 'We issue detailed English invoices that you submit to your insurer for reimbursement. We do not bill insurers directly.',
      },
      {
        q: 'Can you continue a rehabilitation programme I started in another country?',
        a: 'Yes. Bring your surgeon’s or therapist’s protocol and we continue from your current phase, respecting the restrictions they set.',
      },
      {
        q: 'Do you treat children and older adults?',
        a: 'Yes. We run corrective exercise programmes for children (posture, scoliosis, flat feet) and rehabilitation for older adults, including after hip fracture.',
      },
    ],
    related: [
      { label: 'Physiotherapy in Belgrade', href: '/physical-therapy-belgrade' },
      { label: 'Chiropractic and manual therapy', href: '/chiropractor-belgrade' },
      { label: 'Sports injury rehabilitation', href: '/sports-injury-rehabilitation-belgrade' },
      { label: 'Shockwave therapy for heel pain', href: '/heel-spur-shockwave-therapy-belgrade' },
      { label: 'Price list in English', href: '/prices' },
    ],
    whatsappText: 'Hello, I am looking for an English-speaking physiotherapist in Belgrade. Could I book an assessment?',
  },

  // ───────────────────────────────────────────────────────────── 3
  {
    slug: 'chiropractor-belgrade',
    srCounterpart: '/kiropraktika-novi-beograd',
    title: 'Chiropractor Belgrade – Manual Therapy in English | PHYSIOWRLD',
    description:
      'Chiropractor in Belgrade with English-speaking care. Ackerman-method adjustment and manual therapy after a clinical screening, 30 min, 5,000 RSD. Block 64, New Belgrade. Indications and contraindications explained.',
    h1: 'Chiropractic and Manual Therapy in Belgrade',
    subtitle:
      'Spinal and joint adjustments performed by a trained Ackerman chiropractor, always after a screening — and combined with physiotherapy when that gives a better result.',
    breadcrumb: 'Chiropractor Belgrade',
    serviceName: 'Chiropractic adjustment',
    schemaType: 'MedicalTherapy',
    hero: {
      src: '/images/kiropraktika-manuelna-terapija-novi-beograd.webp',
      alt: 'Chiropractor performing a manual spinal adjustment at PHYSIOWRLD in New Belgrade',
      width: 850,
      height: 638,
    },
    intro: [
      'Chiropractic at PHYSIOWRLD is a 30-minute appointment with Stefan Miljković, an Ackerman-method chiropractor, working inside a physiotherapy clinic. That setting matters: every adjustment is preceded by a screening for red flags, and if your problem is better served by exercise, manual soft-tissue work or shockwave, you are told so and treated accordingly rather than adjusted by default.',
      'The Ackerman method is a structural technique that focuses on the pelvis, sacroiliac joints and spine, using low-amplitude, precisely directed thrusts. It is commonly used for mechanical low back pain, restricted spinal segments, pelvic asymmetry and neck stiffness.',
      'We describe what chiropractic can and cannot do honestly. Manual adjustment can reduce pain and restore movement in a mechanically restricted joint. It does not “put a disc back”, and it does not cure herniated discs, arthritis or nerve compression — those conditions need a medical diagnosis and, usually, a combined plan.',
    ],
    whoFor: [
      'Mechanical low back pain and stiffness without neurological signs',
      'Sacroiliac joint pain and pelvic asymmetry',
      'Neck stiffness and tension-type headache of cervical origin',
      'Thoracic (mid-back) stiffness from desk work',
      'Recurrent “locking” of the back after bending or lifting',
      'Patients whose imaging has excluded a serious cause and who want a manual approach',
    ],
    firstVisit: [
      'History and screening: the chiropractor asks about the onset, radiation of pain, numbness or weakness, previous imaging, osteoporosis, anticoagulant use and other factors that decide whether manipulation is safe.',
      'Physical examination: posture, leg-length and pelvic alignment, spinal segment mobility, neurological screen (reflexes, sensation, strength) and orthopaedic tests.',
      'Treatment in the same visit when appropriate. If the screening raises a concern — for example signs of nerve root compression — you are referred for imaging or medical review before any adjustment.',
    ],
    treatment: [
      'Soft-tissue preparation of the paraspinal muscles and hips.',
      'Ackerman-method adjustment of the pelvis and the restricted spinal segments — controlled, specific, low-amplitude thrusts. The audible “click” is gas release in the joint, not bone moving.',
      'Mobilisation without thrust for patients where high-velocity techniques are not appropriate (older adults, anxiety, osteopenia).',
      'Two or three stabilisation exercises so the correction holds between visits.',
    ],
    durationAndCount: [
      'The appointment lasts 30 minutes. Acute mechanical back pain often improves within 1–3 sessions. Chronic or recurrent problems typically follow a plan of 4–6 visits, spaced 5–10 days apart, usually combined with kinesitherapy so that the stabilising muscles do the long-term work. We do not sell open-ended “maintenance” packages.',
    ],
    prices: [price('Chiropractic'), price('Physical therapy'), price('Kinesitherapy'), price('Active Release')],
    conditions: [
      'Acute and chronic mechanical low back pain',
      'Sacroiliac joint dysfunction',
      'Cervicogenic headache and neck stiffness',
      'Thoracic spine stiffness and rib joint restriction',
      'Postural pain from prolonged sitting',
      'Pelvic asymmetry and functional leg-length difference',
    ],
    contraindications: [
      'Osteoporosis or long-term corticosteroid use (thrust techniques are not used)',
      'Recent fracture, spinal surgery or spinal instability',
      'Progressive neurological deficit: worsening weakness, numbness, bladder or bowel changes — this needs urgent medical assessment',
      'Inflammatory arthritis in an active flare, infection or tumour in the spine',
      'Anticoagulant therapy or bleeding disorders (individual assessment)',
      'Vertebral artery symptoms (dizziness, visual disturbance on neck rotation) — cervical manipulation is not performed',
      'Pregnancy: pelvic techniques only after individual assessment; some patients are better served by physiotherapy',
    ],
    sections: [
      {
        id: 'chiropractic-vs-physiotherapy',
        heading: 'Chiropractor or physiotherapist — which do you need?',
        paragraphs: [
          'For a stiff, “locked” back or neck without leg or arm symptoms, chiropractic adjustment often gives the fastest relief. For pain that radiates below the knee or elbow, pain after surgery, tendon problems or general deconditioning, physiotherapy is the primary treatment and manipulation is, at most, an adjunct.',
          'Because both are delivered under one roof, you do not have to choose in advance. The screening at your first visit decides, and many patients receive both within the same plan — for example an adjustment followed by kinesitherapy in the same week.',
        ],
        table: {
          caption: 'Typical decision guide (not a substitute for assessment)',
          headers: ['Situation', 'Usually starts with', 'Why'],
          rows: [
            ['Sudden back lock after lifting, no leg symptoms', 'Chiropractic', 'Mechanical joint restriction responds quickly to adjustment'],
            ['Back pain radiating below the knee, numbness', 'Medical review, then physiotherapy', 'Possible nerve root involvement — imaging may be needed first'],
            ['Neck stiffness and desk-related headache', 'Chiropractic + posture programme', 'Restores segment mobility; exercise prevents recurrence'],
            ['Chronic tendon pain (Achilles, elbow, heel)', 'Physiotherapy / shockwave', 'Not a joint restriction problem'],
            ['After spinal or joint surgery', 'Physiotherapy', 'Manipulation is contraindicated early after surgery'],
          ],
        },
      },
      {
        id: 'safety',
        heading: 'Is chiropractic safe?',
        paragraphs: [
          'When performed after a proper screening on an appropriate patient, spinal manipulation has a low rate of serious complications. Mild soreness for 24–48 hours after the first adjustment is common and settles on its own. Serious adverse events are rare and are associated with treating patients who should have been screened out — which is exactly why our first visit includes a neurological and vascular screen and why we decline to adjust when the criteria are not met.',
        ],
      },
    ],
    faqs: [
      {
        q: 'How much does a chiropractor cost in Belgrade?',
        a: 'At PHYSIOWRLD a chiropractic appointment — screening and adjustment — is 5,000 RSD for 30 minutes. If it is combined with physiotherapy, those sessions are billed at the physiotherapy rate (2,500–3,500 RSD).',
      },
      {
        q: 'Does the chiropractor speak English?',
        a: 'Yes. The screening, explanation of findings and the treatment itself are conducted in English.',
      },
      {
        q: 'Can chiropractic fix a herniated disc?',
        a: 'No. A disc herniation is a structural change that manipulation does not reverse. Manual therapy may reduce associated joint stiffness and pain in selected cases, but disc-related pain with nerve symptoms is primarily managed with physiotherapy and medical care. We assess this at the first visit and refer for imaging when needed.',
      },
      {
        q: 'How many sessions do I need?',
        a: 'Acute mechanical pain often improves in 1–3 sessions. Recurrent or chronic problems typically need 4–6 visits combined with exercise. We do not recommend indefinite maintenance adjustments.',
      },
      {
        q: 'Does the adjustment hurt?',
        a: 'The thrust itself is quick and usually painless. Some patients feel mild soreness for a day or two, similar to after unfamiliar exercise.',
      },
      {
        q: 'Do I need an X-ray or MRI before treatment?',
        a: 'Not routinely. Imaging is requested only when the screening suggests it — for example neurological signs, significant trauma, osteoporosis risk or symptoms that do not fit a mechanical pattern.',
      },
      {
        q: 'Can I be adjusted during pregnancy?',
        a: 'Only after individual assessment, using non-thrust pelvic techniques. In many cases physiotherapy is the more suitable option; we advise on the day.',
      },
    ],
    related: [
      { label: 'Physiotherapy in Belgrade', href: '/physical-therapy-belgrade' },
      { label: 'English-speaking physiotherapist', href: '/english-speaking-physiotherapist-belgrade' },
      { label: 'Sports injury rehabilitation', href: '/sports-injury-rehabilitation-belgrade' },
      { label: 'Price list in English', href: '/prices' },
    ],
    whatsappText: 'Hello, I would like to book a chiropractic appointment in English.',
  },

  // ───────────────────────────────────────────────────────────── 4
  {
    slug: 'sports-injury-rehabilitation-belgrade',
    srCounterpart: '/fizikalna-terapija-novi-beograd',
    title: 'Sports Injury Rehabilitation Belgrade | PHYSIOWRLD',
    description:
      'Sports injury rehabilitation in Belgrade: muscle, tendon and ligament injuries, ankle, knee, shoulder and Achilles. English-speaking physiotherapists, structured return-to-training plans. Block 64, New Belgrade.',
    h1: 'Sports Injury Rehabilitation in Belgrade',
    subtitle:
      'From the first days after injury to the day you are cleared to train again — assessment, hands-on treatment, progressive loading and return-to-sport testing.',
    breadcrumb: 'Sports Injury Rehabilitation',
    serviceName: 'Sports injury rehabilitation',
    schemaType: 'PhysicalTherapy',
    hero: {
      src: '/images/kineziterapija-novi-beograd.webp',
      alt: 'Athlete performing supervised rehabilitation exercise with a physiotherapist at PHYSIOWRLD',
      width: 850,
      height: 500,
    },
    intro: [
      'Sports injuries heal on a biological timeline; whether you come back stronger or keep re-injuring depends on what you do during that time. Our sports rehabilitation programme is built around progressive loading — protecting the tissue early, restoring range and strength in the middle phase, and rebuilding power, speed and confidence before you return to training.',
      'We work with recreational runners, footballers, basketball and tennis players, CrossFit and gym athletes, martial artists and skiers, as well as with people whose “sport” is a physically demanding job. Sessions are one-on-one, 30 minutes, with an English-speaking physiotherapist.',
      'Where a diagnosis is unclear, we arrange ultrasound or MRI through our partner diagnostic clinic and, when needed, an orthopaedic opinion — rehabilitation is planned around a confirmed diagnosis, not a guess.',
    ],
    whoFor: [
      'Muscle strains (hamstring, calf, quadriceps, groin)',
      'Tendon injuries — Achilles, patellar, rotator cuff, tennis elbow',
      'Ligament injuries — ankle sprain, MCL, ACL (conservative or post-op)',
      'Shoulder instability and impingement in overhead athletes',
      'Runner’s knee, shin splints and stress-related overload',
      'Athletes returning after surgery who need a structured return-to-play plan',
    ],
    firstVisit: [
      'Injury history: mechanism, when it happened, what you have done so far, previous injuries to the same area and your training schedule.',
      'Examination: range of motion, strength testing against resistance, ligament stability tests, palpation and — where relevant — functional tests such as single-leg hop or squat mechanics.',
      'Plan: the phase you are in, what is allowed and what is not, an estimated timeline and the criteria you need to meet to progress. Treatment starts the same day.',
    ],
    treatment: [
      'Early phase: swelling control, pain-free range of motion, isometric loading, high-intensity laser or electrotherapy where it helps.',
      'Middle phase: progressive strengthening, manual therapy and Active Release for restricted tissue, balance and neuromuscular control.',
      'Late phase: plyometrics, running and change-of-direction progressions, sport-specific drills.',
      'Return-to-sport: strength symmetry, hop and agility testing before clearance, and a plan for the first weeks back in training.',
    ],
    durationAndCount: [
      'Sessions are 30 minutes. A grade I muscle strain may need 3–5 sessions over two weeks; an ankle sprain 6–8; a tendinopathy 8–12 over 2–3 months; post-operative cases longer. Frequency is highest in the first two weeks and reduces as you take over more of the work independently.',
    ],
    prices: [price('Kinesitherapy'), price('Physical therapy'), price('Active Release'), price('Shockwave therapy'), price('Sports massage')],
    conditions: [
      'Hamstring, calf and groin strains',
      'Achilles tendinopathy and calf–Achilles overload',
      'Patellar tendinopathy (jumper’s knee) and runner’s knee',
      'Lateral ankle sprain and chronic ankle instability',
      'Meniscus and MCL injuries; ACL — conservative and post-reconstruction',
      'Shoulder impingement, rotator cuff tendinopathy and instability',
      'Tennis and golfer’s elbow',
      'Shin splints (medial tibial stress syndrome)',
    ],
    contraindications: [
      'Suspected fracture, complete tendon rupture or joint dislocation — emergency assessment and imaging first',
      'A knee that gives way, locks or cannot fully straighten after injury — orthopaedic review before loading',
      'Rapidly increasing swelling, warmth and fever',
      'Calf pain with swelling after a long flight or immobilisation — rule out thrombosis before treatment',
    ],
    sections: [
      {
        id: 'phases',
        heading: 'The four phases of sports rehabilitation',
        table: {
          headers: ['Phase', 'Goal', 'Typical content', 'Progress when'],
          rows: [
            ['1. Protect', 'Control pain and swelling, keep moving safely', 'Relative rest, isometrics, laser/electrotherapy, gentle range of motion', 'Pain-free walking, swelling stable'],
            ['2. Restore', 'Full range and baseline strength', 'Progressive resistance, manual therapy, balance work', 'Full ROM, strength ≥ 70% of other side'],
            ['3. Rebuild', 'Power, speed, endurance', 'Plyometrics, running progressions, sport-specific loading', 'Strength ≥ 90%, hop tests symmetrical'],
            ['4. Return', 'Safe return to full training and competition', 'Team or sport drills, load monitoring, maintenance programme', 'Cleared on testing and confidence'],
          ],
        },
      },
      {
        id: 'shockwave-for-tendons',
        heading: 'Shockwave therapy for stubborn tendon injuries',
        paragraphs: [
          'Chronic tendinopathies of the Achilles, patellar tendon, plantar fascia and lateral elbow that have not responded to 8–12 weeks of loading often respond to radial shockwave therapy (3–5 sessions, 3,000 RSD each) combined with continued exercise. Shockwave is not used in the acute phase or on a recently injured muscle.',
        ],
      },
      {
        id: 'sports-massage',
        heading: 'Sports massage and recovery',
        paragraphs: [
          'Sports massage (30 minutes 3,500 RSD, 60 minutes 5,000 RSD) is available for training recovery and pre-competition preparation. It complements rehabilitation but does not replace strengthening — a tight muscle that keeps returning is usually a weak or overloaded one.',
        ],
      },
    ],
    faqs: [
      {
        q: 'How soon after an injury should I come in?',
        a: 'Within the first 2–5 days, once a fracture or serious structural injury has been excluded. Early guidance on what to load and what to protect shortens the total recovery time.',
      },
      {
        q: 'Do you provide return-to-sport testing?',
        a: 'Yes. Before clearance we test strength symmetry, single-leg hop and, where relevant, agility and change-of-direction, and give you the results in writing.',
      },
      {
        q: 'How much does sports injury rehabilitation cost?',
        a: 'Kinesitherapy sessions are 3,500 RSD (10 sessions 30,000 RSD); physical therapy sessions with device treatment are 2,500 RSD. The assessment is part of the first session.',
      },
      {
        q: 'Can I keep training while injured?',
        a: 'Usually yes, with modifications. Part of the first visit is agreeing what you can safely continue — cycling instead of running, upper-body work during a lower-limb injury, and so on.',
      },
      {
        q: 'Do I need an MRI before starting?',
        a: 'Not always. Many injuries are diagnosed clinically. If the examination suggests a structural tear or the recovery does not follow the expected course, we arrange imaging through our partner clinic nearby.',
      },
      {
        q: 'Do you work with athletes who had surgery abroad?',
        a: 'Yes. Bring your surgeon’s protocol and operative report; we continue rehabilitation from your current phase and respect the restrictions set by the surgeon.',
      },
      {
        q: 'Is the physiotherapist English-speaking?',
        a: 'Yes — assessment, exercise coaching and written programmes are in English.',
      },
    ],
    related: [
      { label: 'ACL and post-operative knee rehabilitation', href: '/acl-knee-rehabilitation-belgrade' },
      { label: 'Shockwave therapy for tendon and heel pain', href: '/heel-spur-shockwave-therapy-belgrade' },
      { label: 'Physiotherapy in Belgrade', href: '/physical-therapy-belgrade' },
      { label: 'Chiropractic and manual therapy', href: '/chiropractor-belgrade' },
      { label: 'Price list in English', href: '/prices' },
    ],
    whatsappText: 'Hello, I have a sports injury and would like to book an assessment in English.',
  },

  // ───────────────────────────────────────────────────────────── 5
  {
    slug: 'acl-knee-rehabilitation-belgrade',
    srCounterpart: '/rehabilitacija-posle-operacije-kolena-beograd',
    title: 'ACL Rehabilitation Belgrade – Post-Op Knee Physio | PHYSIOWRLD',
    description:
      'ACL and post-operative knee rehabilitation in Belgrade: after ACL reconstruction, meniscus surgery and knee replacement. Swelling control, range of motion, strength and return to running. English-speaking physiotherapists, 3,500 RSD per session.',
    h1: 'ACL and Post-Operative Knee Rehabilitation in Belgrade',
    subtitle:
      'Phase-based rehabilitation after ACL reconstruction, meniscus repair or arthroscopy — coordinated with your surgeon, one-on-one with an English-speaking physiotherapist.',
    breadcrumb: 'ACL & Knee Rehabilitation',
    serviceName: 'Post-operative knee rehabilitation',
    schemaType: 'PhysicalTherapy',
    hero: {
      src: '/images/services/postoperativna-rehabilitacija.webp',
      alt: 'Physiotherapist guiding knee rehabilitation exercise after ACL surgery at PHYSIOWRLD',
      width: 1200,
      height: 800,
    },
    intro: [
      'The outcome of knee surgery is decided as much in the months of rehabilitation as in the operating theatre. After ACL reconstruction, meniscus repair or arthroscopy, the goals follow a predictable order: control swelling, regain full extension and flexion, rebuild quadriceps and hamstring strength, restore balance and control, and only then return to running, cutting and sport.',
      'Our post-operative knee programme is delivered one-on-one in 30-minute sessions with an English-speaking physiotherapist, following the restrictions set by your operating surgeon. Patients who had surgery abroad and are recovering in Belgrade are welcome — bring your operative report and protocol.',
      'Each phase has objective criteria for moving on. This prevents both the most common mistakes: progressing too fast on a swollen, weak knee, and staying cautious for so long that the muscle never fully returns.',
    ],
    whoFor: [
      'After ACL reconstruction (hamstring, patellar tendon or quadriceps graft)',
      'After meniscus repair or partial meniscectomy',
      'After knee arthroscopy for cartilage or loose bodies',
      'After total or partial knee replacement',
      'Conservative (non-surgical) management of ACL or meniscus injury',
      'Patients whose recovery has stalled — persistent stiffness, weakness or lack of confidence months after surgery',
    ],
    firstVisit: [
      'We review the operative report and your surgeon’s protocol, including weight-bearing status, brace instructions and any range-of-motion limits.',
      'Examination: swelling (circumference), knee extension and flexion, quadriceps activation, gait, scar mobility and — later in rehabilitation — strength and hop testing.',
      'You receive a written phase plan, a home programme and your first treatment. Ideally the first visit takes place within the first week after surgery, but we also take over rehabilitation at any later stage.',
    ],
    treatment: [
      'Weeks 0–2: swelling control, patellar and scar mobilisation, restoring full extension, quadriceps activation (with electrostimulation if needed), gait training with crutches.',
      'Weeks 2–6: progressive flexion, closed-chain strengthening, stationary cycling, balance and proprioception, normalising walking.',
      'Weeks 6–12: heavier strength work (squat, leg press, hamstring loading), single-leg control, introduction of straight-line jogging when criteria are met.',
      'Months 3–6+: plyometrics, change of direction, sport-specific drills and return-to-sport testing (strength symmetry, hop battery, movement quality).',
    ],
    durationAndCount: [
      'Sessions are 30 minutes at 3,500 RSD (10 sessions 30,000 RSD). Meniscectomy and simple arthroscopy usually need 6–10 sessions over 4–6 weeks. ACL reconstruction is a 6–9-month process; most patients attend 2–3 times per week in the first 6 weeks, then weekly, with increasing independent training. Knee replacement typically requires 10–20 sessions in the first three months.',
    ],
    prices: [price('Post-operative rehabilitation'), price('Kinesitherapy'), price('Electrotherapy'), price('High-intensity laser')],
    conditions: [
      'ACL reconstruction rehabilitation',
      'Meniscus repair and meniscectomy',
      'Knee arthroscopy (cartilage, loose bodies, plica)',
      'Total and partial knee replacement',
      'MCL and PCL injuries',
      'Patellar dislocation and stabilisation surgery',
      'Persistent stiffness or quadriceps weakness after knee surgery',
    ],
    contraindications: [
      'Signs of infection: increasing redness, heat, discharge from the wound, fever — contact your surgeon immediately',
      'Sudden calf pain and swelling — possible deep-vein thrombosis, medical assessment first',
      'A sudden “pop”, new instability or a rapidly swelling knee during rehabilitation — surgical review before continuing',
      'Progressing beyond your surgeon’s restrictions — we do not override the operating surgeon’s protocol',
    ],
    sections: [
      {
        id: 'milestones',
        heading: 'ACL rehabilitation milestones',
        table: {
          caption: 'Typical timeline after ACL reconstruction — individual protocols vary by graft and surgeon',
          headers: ['Timeframe', 'Key goals', 'Criteria to progress'],
          rows: [
            ['Week 0–2', 'Full extension, minimal swelling, quad activation', 'Extension equal to other side, straight-leg raise without lag'],
            ['Week 2–6', 'Flexion to 120°+, normal gait without crutches', 'No limp, swelling controlled after activity'],
            ['Week 6–12', 'Strength building, single-leg control', 'Quad strength ≥ 70% of other side, pain-free single-leg squat'],
            ['Month 3–4', 'Straight-line running', 'Strength ≥ 80%, no swelling response to loading'],
            ['Month 4–6', 'Plyometrics, change of direction', 'Hop tests ≥ 90% symmetry, good landing mechanics'],
            ['Month 6–9+', 'Return to sport', 'Strength and hop symmetry ≥ 90%, sport-specific testing, psychological readiness'],
          ],
        },
      },
      {
        id: 'common-mistakes',
        heading: 'Common mistakes after knee surgery',
        bullets: [
          'Accepting a knee that does not fully straighten — loss of extension is the hardest deficit to recover later.',
          'Judging recovery by pain alone. A pain-free knee at 3 months can still have 30% quadriceps weakness; we test rather than assume.',
          'Returning to sport by the calendar instead of by criteria — re-injury risk is significantly higher when strength and hop symmetry are not met.',
          'Stopping rehabilitation once walking is normal, usually around week 6–8, which is exactly when strength work should intensify.',
        ],
      },
      {
        id: 'meniscus-and-replacement',
        heading: 'Meniscus surgery and knee replacement',
        paragraphs: [
          'After a partial meniscectomy, weight-bearing is usually allowed immediately and rehabilitation focuses on swelling, range of motion and quadriceps strength over 4–6 weeks. After a meniscus repair, the surgeon typically limits flexion under load for 4–6 weeks to protect the sutured tissue — the timeline is slower and we follow it strictly.',
          'After total knee replacement, the priorities are early extension and flexion (a target of 0–110° or more in the first weeks), controlled swelling, walking pattern and stair technique. Most patients attend 2–3 times per week initially; laser and electrotherapy are used for pain and swelling alongside exercise.',
        ],
      },
    ],
    faqs: [
      {
        q: 'When should I start physiotherapy after ACL surgery?',
        a: 'Ideally within the first week. Early work on swelling, extension and quadriceps activation makes the later phases easier. If you are already weeks or months post-op, we take over from your current stage.',
      },
      {
        q: 'How long does ACL rehabilitation take?',
        a: 'Return to running is usually possible around month 3–4 when strength criteria are met; return to pivoting sports typically at 6–9 months, after passing strength and hop testing. Rushing this stage is the main cause of re-injury.',
      },
      {
        q: 'How much does post-operative knee rehabilitation cost?',
        a: '3,500 RSD per 30-minute session, or 30,000 RSD for a package of 10. Electrotherapy or laser used within the session is included; a standalone laser session is 2,500 RSD.',
      },
      {
        q: 'I had my surgery in another country. Can you continue my rehabilitation?',
        a: 'Yes. Bring the operative report and the surgeon’s protocol. We follow their restrictions and communicate with them if something is unclear.',
      },
      {
        q: 'Do I need a brace or crutches, and for how long?',
        a: 'This is set by your surgeon and depends on the procedure. Most ACL patients stop crutches within 1–3 weeks once they can walk without a limp; braces are used variably. We help you progress off them safely.',
      },
      {
        q: 'Can I do the exercises at home instead?',
        a: 'Home exercise is an essential part of the plan, but supervised sessions are where we test, correct technique and progress the load. Most patients do best with 1–3 supervised sessions per week plus a daily home programme.',
      },
      {
        q: 'When can I drive after knee surgery?',
        a: 'For a right knee, usually when you can walk without crutches and perform an emergency stop comfortably — often 2–4 weeks after arthroscopy and 4–6 weeks after ACL reconstruction. Confirm with your surgeon and insurer.',
      },
      {
        q: 'Do you offer return-to-sport testing?',
        a: 'Yes — isometric strength symmetry, a single-leg hop battery and movement-quality assessment, with a written report you can share with your coach or surgeon.',
      },
    ],
    related: [
      { label: 'Sports injury rehabilitation', href: '/sports-injury-rehabilitation-belgrade' },
      { label: 'Physiotherapy in Belgrade', href: '/physical-therapy-belgrade' },
      { label: 'English-speaking physiotherapist', href: '/english-speaking-physiotherapist-belgrade' },
      { label: 'Shockwave therapy', href: '/heel-spur-shockwave-therapy-belgrade' },
      { label: 'Price list in English', href: '/prices' },
    ],
    whatsappText: 'Hello, I am recovering from knee surgery and would like to book post-operative rehabilitation in English.',
  },

  // ───────────────────────────────────────────────────────────── 6
  {
    slug: 'heel-spur-shockwave-therapy-belgrade',
    srCounterpart: '/shockwave-terapija-beograd',
    title: 'Heel Spur and Shockwave Therapy Belgrade | PHYSIOWRLD',
    description:
      'Shockwave therapy in Belgrade for heel spur, plantar fasciitis and Achilles tendinopathy. Radial ESWT, 3–5 sessions, 3,000 RSD per session or 13,500 RSD for five. English-speaking physiotherapists, Block 64 New Belgrade.',
    h1: 'Shockwave Therapy for Heel Pain in Belgrade',
    subtitle:
      'Radial shockwave (ESWT) for plantar fasciitis, heel spur, Achilles and other chronic tendon problems — combined with a loading programme, not used as a stand-alone quick fix.',
    breadcrumb: 'Heel Spur & Shockwave',
    serviceName: 'Shockwave therapy (radial ESWT)',
    schemaType: 'MedicalTherapy',
    hero: {
      src: '/images/blog/plantarni-fascitis-shockwave-cover.webp',
      alt: 'Radial shockwave therapy applied to the heel for plantar fasciitis at PHYSIOWRLD, New Belgrade',
      width: 1280,
      height: 720,
    },
    intro: [
      'Heel pain that is worst with the first steps in the morning and after sitting is most often plantar fasciitis — a degenerative overload of the fascia where it attaches to the heel bone. A “heel spur” seen on X-ray is a consequence of that long-standing traction, not usually the cause of the pain itself, which is why removing the spur is rarely necessary.',
      'Radial extracorporeal shockwave therapy (r-ESWT) delivers acoustic pressure waves to the painful attachment, stimulating a healing response in chronic, poorly healing tendon and fascia tissue. It is one of the best-supported non-surgical treatments for plantar fasciitis lasting more than three months, and is also used for Achilles tendinopathy, patellar tendinopathy, tennis elbow and calcific shoulder tendinitis.',
      'At PHYSIOWRLD, shockwave is delivered by a physiotherapist as part of a plan that includes calf and plantar fascia loading, footwear advice and, where needed, laser therapy. We use radial (not focused) shockwave, which is appropriate for these superficial, soft-tissue conditions.',
    ],
    whoFor: [
      'Plantar fasciitis / heel spur lasting more than 6–12 weeks',
      'Achilles tendinopathy (mid-portion and insertional)',
      'Patellar tendinopathy (jumper’s knee)',
      'Lateral and medial epicondylitis (tennis and golfer’s elbow)',
      'Calcific tendinitis of the shoulder',
      'Greater trochanteric pain syndrome (lateral hip pain)',
      'Chronic muscle trigger points that have not responded to manual therapy',
    ],
    firstVisit: [
      'History: how long the pain has lasted, morning pain pattern, footwear, running or standing load, previous treatments and any injections.',
      'Examination: palpation of the plantar fascia insertion and Achilles, calf flexibility, ankle mobility, foot posture and — when indicated — a check for other causes of heel pain such as nerve entrapment or stress fracture.',
      'If shockwave is appropriate, the first session is usually delivered the same day, together with your loading programme. If the picture does not fit, we arrange imaging (ultrasound or X-ray) through our partner clinic before treating.',
    ],
    treatment: [
      'The painful area is located by palpation and marked. Coupling gel is applied and the applicator delivers roughly 2,000–2,500 pulses over 5–8 minutes at a pressure adjusted to your tolerance.',
      'The treatment is uncomfortable but tolerable; intensity is increased gradually. No anaesthetic is used because it reduces the effect.',
      'Afterwards you can walk normally. Mild soreness or redness for 24–48 hours is expected. You avoid anti-inflammatory medication and ice for the following days because the treatment works by stimulating a controlled inflammatory healing response.',
      'Each visit also includes progression of your calf and plantar fascia loading exercises — the combination is what produces lasting results.',
    ],
    durationAndCount: [
      'A shockwave session lasts 15–20 minutes. The standard protocol is 3–5 sessions, one per week. Improvement is usually noticeable after the second or third session, with the full effect developing over 6–12 weeks as the tissue remodels. Sessions cost 3,000 RSD each or 13,500 RSD for a package of five — a complete course therefore costs 9,000–13,500 RSD.',
    ],
    prices: [price('Shockwave therapy'), price('Physical therapy'), price('High-intensity laser'), price('Kinesitherapy')],
    conditions: [
      'Plantar fasciitis and heel spur',
      'Achilles tendinopathy',
      'Patellar tendinopathy',
      'Tennis elbow and golfer’s elbow',
      'Calcific shoulder tendinitis',
      'Greater trochanteric pain syndrome',
      'Shin splints (medial tibial stress syndrome)',
      'Chronic myofascial trigger points',
    ],
    contraindications: [
      'Pregnancy',
      'Anticoagulant therapy or bleeding disorders',
      'Local infection, open wound or tumour in the treatment area',
      'Cortisone injection into the same area within the previous 6 weeks',
      'Acute tendon rupture or stress fracture — needs orthopaedic care, not shockwave',
      'Treatment directly over the lungs, major nerves or blood vessels, or over growth plates in children',
      'Pacemaker or other implanted electronic device (individual assessment)',
    ],
    sections: [
      {
        id: 'orthopaedic-vs-aesthetic',
        heading: 'Orthopaedic shockwave vs. aesthetic AWT — not the same treatment',
        paragraphs: [
          'The same physical principle — acoustic pressure waves — is used in two very different services at our clinic, and they should not be confused. Orthopaedic shockwave (this page) treats painful tendons and fascia; the parameters, applicators and protocol are set for tissue healing and pain. Acoustic wave therapy (AWT) for cellulite and body contouring is an aesthetic treatment with different settings and goals, listed separately under body-care services at 3,500 RSD per region.',
          'If you are looking for treatment of heel, Achilles, elbow or shoulder pain, you are on the right page. For cellulite treatment, see the anti-cellulite page.',
        ],
      },
      {
        id: 'evidence',
        heading: 'What the evidence says about shockwave for plantar fasciitis',
        paragraphs: [
          'Systematic reviews and clinical guidelines support ESWT for chronic plantar fasciitis that has not improved with at least 3 months of conservative care, with success rates commonly reported in the range of 60–80% for meaningful pain reduction. Results are better when shockwave is combined with plantar fascia and calf stretching or loading, and when footwear and training load are addressed. It is not effective in every patient, and we say so — if there is no change after 3 sessions, we reassess rather than continuing.',
        ],
      },
      {
        id: 'compare',
        heading: 'Shockwave compared with other heel-pain treatments',
        table: {
          headers: ['Treatment', 'Typical role', 'Considerations'],
          rows: [
            ['Loading exercises + footwear', 'First-line for every patient', 'Slow (8–12 weeks) but essential; shockwave does not replace it'],
            ['Radial shockwave (ESWT)', 'Chronic cases > 6–12 weeks', '3–5 sessions, no downtime, no medication; full effect over 6–12 weeks'],
            ['High-intensity laser', 'Pain relief, acute-on-chronic flares', 'Often combined with shockwave; 2,500 RSD per session'],
            ['Cortisone injection', 'Short-term pain relief', 'Risk of fascia rupture and fat-pad atrophy with repeat injections; not compatible with shockwave for 6 weeks'],
            ['Surgery', 'Rare, resistant cases after 12+ months', 'Last resort; long recovery'],
          ],
        },
      },
      {
        id: 'achilles',
        heading: 'Shockwave for Achilles tendinopathy',
        paragraphs: [
          'Mid-portion Achilles tendinopathy responds well to a combination of heavy slow calf loading and shockwave; insertional Achilles problems (pain at the heel bone) need a modified exercise approach that avoids deep dorsiflexion. In both cases, the shockwave protocol is 3–5 weekly sessions. We do not treat a suspected partial or complete Achilles rupture with shockwave — that requires ultrasound assessment and orthopaedic care first.',
        ],
      },
    ],
    faqs: [
      {
        q: 'How much does shockwave therapy cost in Belgrade?',
        a: 'At PHYSIOWRLD a shockwave session is 3,000 RSD; a package of five is 13,500 RSD. A complete course for heel pain (3–5 sessions) therefore costs 9,000–13,500 RSD.',
      },
      {
        q: 'How many shockwave sessions are needed for a heel spur or plantar fasciitis?',
        a: 'Usually 3–5 sessions, one per week. Most patients notice improvement after the second or third session; the full effect develops over the following 6–12 weeks.',
      },
      {
        q: 'Does shockwave therapy hurt?',
        a: 'It is uncomfortable rather than painful — a strong tapping sensation that is increased gradually to your tolerance. Sessions last 15–20 minutes, and you walk out normally.',
      },
      {
        q: 'Does shockwave remove the heel spur?',
        a: 'No, and it does not need to. The spur is a marker of long-standing traction, not usually the pain source. Shockwave treats the painful fascia attachment; pain typically resolves while the spur remains on X-ray.',
      },
      {
        q: 'Can I run or train during shockwave treatment?',
        a: 'Light activity is fine; we usually advise reducing the specific aggravating load (long runs, prolonged standing) during the course and rebuilding it gradually as symptoms settle.',
      },
      {
        q: 'Is your shockwave focused or radial?',
        a: 'Radial (r-ESWT). It is well suited to superficial conditions such as plantar fasciitis, Achilles and elbow tendinopathy. We do not offer focused shockwave.',
      },
      {
        q: 'I had a cortisone injection recently. Can I have shockwave?',
        a: 'Not within 6 weeks of the injection, because the combination increases the risk of tissue damage. We schedule accordingly.',
      },
      {
        q: 'Is this the same as the AWT cellulite treatment?',
        a: 'No. Orthopaedic shockwave treats painful tendons and fascia. AWT for cellulite is an aesthetic treatment with different parameters, offered separately under body-care services.',
      },
    ],
    related: [
      { label: 'Sports injury rehabilitation', href: '/sports-injury-rehabilitation-belgrade' },
      { label: 'Physiotherapy in Belgrade', href: '/physical-therapy-belgrade' },
      { label: 'English-speaking physiotherapist', href: '/english-speaking-physiotherapist-belgrade' },
      { label: 'ACL rehabilitation', href: '/acl-knee-rehabilitation-belgrade' },
      { label: 'Price list in English', href: '/prices' },
    ],
    whatsappText: 'Hello, I have heel pain and would like to book shockwave therapy in English.',
  },
];

export const EN_PRICES_PAGE = {
  slug: 'prices',
  srCounterpart: '/cenovnik',
  title: 'Physiotherapy Prices Belgrade – English Price List | PHYSIOWRLD',
  description:
    'PHYSIOWRLD price list in English: physical therapy 2,500 RSD, kinesitherapy 3,500 RSD, chiropractic 5,000 RSD, shockwave 3,000 RSD, massage from 3,000 RSD, lymphatic drainage 3,500 RSD, pressotherapy 2,000 RSD. Block 64, New Belgrade.',
  h1: 'Physiotherapy Price List in Belgrade',
  subtitle:
    'Transparent 2026 prices in RSD for every service at PHYSIOWRLD, Block 64, New Belgrade. Assessment is included in the first session; packages have no expiry date.',
  faqs: [
    { q: 'Is the initial assessment charged separately?', a: 'No. The clinical assessment is part of your first session. The examination is free; only treatment is charged.' },
    { q: 'Do packages expire?', a: 'No. Packages are paid at the first appointment and have no expiry date.' },
    { q: 'Can I pay by card?', a: 'Yes — cash and card are accepted, and an itemised invoice in English is available for insurance reimbursement.' },
    { q: 'Are prices the same for foreigners?', a: 'Yes. Prices are identical for every patient regardless of nationality or residence.' },
    { q: 'What does a complete shockwave course cost?', a: 'A typical 3–5 session course costs 9,000–13,500 RSD (3,000 RSD per session, 13,500 RSD for a package of five).' },
    { q: 'What does ACL rehabilitation cost in total?', a: 'Post-operative sessions are 3,500 RSD (30,000 RSD for 10). A full ACL programme over 6–9 months typically involves 20–40 supervised sessions depending on how much you train independently.' },
  ] as EnFaq[],
  whatsappText: 'Hello, I would like to book an appointment. Could you tell me the available times?',
};

/** SR page → EN page map, used by SEOHead (hreflang) and the language switcher. */
export const EN_COUNTERPARTS: Record<string, string> = Object.fromEntries([
  ...EN_LANDINGS.map((l) => [l.srCounterpart, `/${l.slug}`]),
  [EN_PRICES_PAGE.srCounterpart, `/${EN_PRICES_PAGE.slug}`],
]);

/** EN page → SR page map. */
export const SR_COUNTERPARTS: Record<string, string> = Object.fromEntries([
  ...EN_LANDINGS.map((l) => [`/${l.slug}`, l.srCounterpart]),
  [`/${EN_PRICES_PAGE.slug}`, EN_PRICES_PAGE.srCounterpart],
]);

export const EN_ROUTES = [...EN_LANDINGS.map((l) => `/${l.slug}`), `/${EN_PRICES_PAGE.slug}`];

export const whatsappLink = WA;

/* ────────────────────────── JSON-LD builders ────────────────────────── */

export function clinicSchema() {
  return {
    '@type': 'MedicalClinic',
    '@id': `${SITE_URL}/#clinic`,
    name: CLINIC.name,
    url: SITE_URL,
    logo: CLINIC.logo,
    image: CLINIC.ogImage,
    telephone: CLINIC.phoneE164,
    email: CLINIC.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: CLINIC.street,
      addressLocality: CLINIC.city,
      addressRegion: 'Beograd',
      postalCode: CLINIC.postalCode,
      addressCountry: CLINIC.country,
    },
    geo: { '@type': 'GeoCoordinates', latitude: CLINIC.geo.lat, longitude: CLINIC.geo.lng },
    openingHoursSpecification: CLINIC.hours.map((h) => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: h.dayOfWeek,
      opens: h.open,
      closes: h.close,
    })),
    sameAs: CLINIC.sameAs,
    availableLanguage: ['en', 'sr', 'ru'],
    areaServed: [
      { '@type': 'City', name: 'Belgrade' },
      { '@type': 'Place', name: 'Novi Beograd' },
    ],
    medicalSpecialty: 'Physiotherapy',
  };
}

function priceNumber(p: string) {
  return p.replace(/,/g, '').match(/\d+/)?.[0] ?? '0';
}

export function landingSchema(l: EnLanding) {
  const url = `${BASE_URL}/${l.slug}`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      clinicSchema(),
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: l.title,
        description: l.description,
        inLanguage: 'en',
        isPartOf: { '@id': `${BASE_URL}/#website` },
        about: { '@id': `${url}#service` },
        primaryImageOfPage: `${BASE_URL}${l.hero.src}`,
      },
      {
        '@type': l.schemaType === 'Service' ? 'Service' : ['Service', l.schemaType],
        '@id': `${url}#service`,
        name: l.serviceName,
        serviceType: l.serviceName,
        description: l.description,
        url,
        inLanguage: 'en',
         provider: { '@id': `${SITE_URL}/#clinic` },
        areaServed: { '@type': 'City', name: 'Belgrade' },
        availableLanguage: ['en', 'sr', 'ru'],
        offers: l.prices.map((p) => ({
          '@type': 'Offer',
          name: p.service,
          price: priceNumber(p.price),
          priceCurrency: 'RSD',
          availability: 'https://schema.org/InStock',
          url,
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Physiotherapy in Belgrade (English)', item: `${BASE_URL}/physical-therapy-belgrade` },
          { '@type': 'ListItem', position: 3, name: l.breadcrumb, item: url },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: l.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };
}

export function pricesSchema() {
  const url = `${BASE_URL}/${EN_PRICES_PAGE.slug}`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      clinicSchema(),
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: EN_PRICES_PAGE.title,
        description: EN_PRICES_PAGE.description,
        inLanguage: 'en',
      },
      {
        '@type': 'OfferCatalog',
        name: 'PHYSIOWRLD price list (English)',
        url,
        itemListElement: EN_PRICES.map((p) => ({
          '@type': 'Offer',
          name: p.service,
          price: priceNumber(p.price),
          priceCurrency: 'RSD',
          availability: 'https://schema.org/InStock',
           offeredBy: { '@id': `${SITE_URL}/#clinic` },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Physiotherapy in Belgrade (English)', item: `${BASE_URL}/physical-therapy-belgrade` },
          { '@type': 'ListItem', position: 3, name: 'Prices', item: url },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: EN_PRICES_PAGE.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };
}

/** Head metadata shared by the prerender script and the runtime SEOHead. */
export function headMeta(page: { slug: string; title: string; description: string; srCounterpart: string; image?: string }) {
  const url = `${BASE_URL}/${page.slug}`;
  const sr = `${SITE_URL}${page.srCounterpart}`;
  return {
    url,
    title: page.title,
    description: page.description,
    canonical: url,
    hreflang: { en: url, sr, ru: sr, 'x-default': sr },
    ogImage: page.image ? `${BASE_URL}${page.image}` : CLINIC.ogImage,
  };
}
